import { type QueryResult, useLazyQuery } from '@apollo/client';
import { useCallback } from 'react';
import validator from 'validator';

import {
  EMAIL_VALIDATION_STATUS,
  type ValidateEmailQuery,
  type ValidateEmailQueryVariables,
} from '~/__gql__/graphql';
import useTranslation from '~/shared/hooks/useTranslation';

import { queryValidateEmail } from './api';

type ValidateEmailQueryResult = QueryResult<ValidateEmailQuery, ValidateEmailQueryVariables>;

const INVALID_EMAIL_VALIDATION_STATES = [
  EMAIL_VALIDATION_STATUS.invalidDomain,
  EMAIL_VALIDATION_STATUS.invalidFormat,
];

const isEmpty = (value: unknown): boolean => !value;
const isString = (value: unknown): value is string => typeof value === 'string';
const isInvalid = ({ data }: ValidateEmailQueryResult): boolean =>
  data?.validateEmail !== undefined && INVALID_EMAIL_VALIDATION_STATES.includes(data.validateEmail);

export const useEmailValidator = () => {
  const { t } = useTranslation();
  const [query, queryResult] = useLazyQuery(queryValidateEmail);

  const errorMessage = t('validation:email');

  const validateFormat = useCallback(
    (email: unknown) => {
      // ignore empty email (should be handled by a different check)
      if (isEmpty(email)) return undefined;
      if (isString(email) && validator.isEmail(email)) return undefined;
      return errorMessage;
    },
    [errorMessage],
  );

  const validateDomain = useCallback(
    async (email: unknown) => {
      if (isEmpty(email)) return undefined;
      return isString(email) &&
        validator.isEmail(email) &&
        isInvalid(await query({ variables: { input: email } }))
        ? errorMessage
        : undefined;
    },
    [query, errorMessage],
  );

  return {
    validateFormat,
    validateDomain,
    error: isInvalid(queryResult) ? errorMessage : undefined,
    isValidating: queryResult.loading,
  };
};
